const EVENT_LABELS = {
  signal_lost: "Sinal perdido",
  signal_locked: "Sinal recuperado",
  return_path_complete: "Retorno concluído",
  download_complete: "Download concluído",
  download_started: "Download iniciado",
  download_failed: "Falha no download",
  force_download: "Download forçado",
  dsmcc_started: "DSM-CC iniciado",
  dsmcc_stopped: "DSM-CC interrompido",
  tuned: "Sintonização concluída",
  startup: "Dispositivo iniciado",
  shutdown: "Dispositivo desligado",
  restart: "Dispositivo reiniciado",
  restarted: "Dispositivo reiniciado",
  device_restarted: "Dispositivo reiniciado",
};

const DETAIL_LABELS = {
  transaction_id: "Transação",
  download_number: "Número do download",
  download_window: "Janela de download",
  download_scenario: "Cenário de download",
  download_folder: "Pasta de download",
  expected_file_count: "Arquivos esperados",
  file_count: "Arquivos recebidos",
  empty_file_count: "Arquivos vazios",
  files: "Arquivos",
  failed_files: "Arquivos com falha",
  total_files: "Total de arquivos",
  success_count: "Sucessos",
  fail_count: "Falhas",
  status: "Resultado",
  reason: "Motivo",
  pid: "PID",
  mode: "Modo",
  frequency_mhz: "Frequência (MHz)",
  dsmcc_pid: "PID DSM-CC",
  dsmcc_pid_hex: "PID DSM-CC hexadecimal",
  version: "Versão",
  total_downloads: "Total de downloads",
  downloads: "Downloads",
  total_packets: "Total de pacotes",
  packets: "Pacotes",
  total_sections: "Total de seções",
  sections: "Seções",
  total_injected: "Total injetado",
  injected: "Injetado",
  sections_injected: "Seções injetadas",
  continuity_errors: "Erros de continuidade",
  corrupted_packets: "Pacotes corrompidos",
  write_success: "Escrita concluída",
  download_percentage: "Percentual do download",
  download_complete: "Download concluído",
  has_signal: "Possui sinal",
  has_lock: "Possui lock",
  strength_percent: "Força do sinal (%)",
  connected: "Conexão",
};

function normalizeEventType(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function fallbackEventLabel(value) {
  const normalized = normalizeEventType(value);
  if (!normalized) return "—";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function detailLabel(value) {
  const normalized = normalizeEventType(value);
  return DETAIL_LABELS[normalized] || fallbackEventLabel(normalized);
}

function scalarValue(value) {
  if (value === true) return "Sim";
  if (value === false) return "Não";
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function arrayItemLabel(item) {
  if (item === null || item === undefined) return "—";
  if (typeof item !== "object") return scalarValue(item);
  return scalarValue(
    item.fileName
      || item.file_name
      || item.name
      || item.id
      || Object.entries(item)
        .map(([key, value]) => `${detailLabel(key)}: ${scalarValue(value)}`)
        .join(", "),
  );
}

function detailValue(value) {
  if (Array.isArray(value)) {
    if (value.length === 0) return "Nenhum";
    return value.map(arrayItemLabel).join("; ");
  }
  if (value && typeof value === "object") {
    return Object.entries(value)
      .map(([key, nestedValue]) => `${detailLabel(key)}: ${detailValue(nestedValue)}`)
      .join("; ");
  }
  return scalarValue(value);
}

function flattenDetails(value, prefix = "", depth = 0) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value).flatMap(([key, nestedValue]) => {
    const label = prefix ? `${prefix} · ${detailLabel(key)}` : detailLabel(key);
    const canDescend = nestedValue
      && typeof nestedValue === "object"
      && !Array.isArray(nestedValue)
      && depth < 2;
    if (canDescend) return flattenDetails(nestedValue, label, depth + 1);
    return [{ label, value: detailValue(nestedValue) }];
  });
}

export function isHistoryEvent(row) {
  if (row?.record_type) return row.record_type === "event";
  if (row?.event) return true;
  const reportType = normalizeEventType(row?.reportType);
  return Boolean(reportType && reportType !== "status_report");
}

export function historyRecordTypeLabel(row) {
  return isHistoryEvent(row) ? "Evento" : "Status";
}

export function historyEventLabel(row) {
  if (!isHistoryEvent(row)) return "—";
  const eventType = normalizeEventType(row?.event || row?.reportType);
  return EVENT_LABELS[eventType] || fallbackEventLabel(eventType);
}

// Mantém compatibilidade com imports anteriores durante a transição.
export const historyReportLabel = historyEventLabel;

export function historyMessageLabel(row) {
  return isHistoryEvent(row) && row?.message ? String(row.message) : "—";
}

export function historyConnectionState(row) {
  const value = isHistoryEvent(row) ? row?.snapshot?.connected : row?.connected;
  return typeof value === "boolean" ? value : null;
}

export function historyConnectionLabel(row) {
  const state = historyConnectionState(row);
  if (state === null) return "—";
  return state ? "Conectado" : "Desconectado";
}

export function historySignalLabel(row) {
  const eventType = normalizeEventType(row?.event || row?.reportType);
  const signal = isHistoryEvent(row)
    ? row?.snapshot?.status?.signal || row?.details?.signal || {}
    : row?.status?.signal || {};

  if (signal.has_signal === false || signal.has_lock === false) return "Sem sinal";
  const cnr = signal.cnr_db ?? signal.rf_cnr_db;
  if (cnr !== undefined && cnr !== null) return `CNR ${cnr} dB`;
  if (signal.strength_percent > 0) return `${signal.strength_percent}% sinal`;
  if (signal.has_signal === true || signal.has_lock === true) return "Sinal ativo";
  if (eventType === "signal_lost") return "Sem sinal";
  if (eventType === "signal_locked") return "Sinal recuperado";

  const frequency = signal.frequency_mhz ?? row?.details?.frequency_mhz;
  if (frequency !== undefined) return `${frequency} MHz`;
  return "—";
}

export function historyDetailEntries(row) {
  if (!isHistoryEvent(row)) return [];
  const details = flattenDetails(row?.details || {});
  const snapshot = row?.snapshot || {};
  const snapshotEntries = [];
  if (typeof snapshot.connected === "boolean") {
    snapshotEntries.push({
      label: "Snapshot · Conexão",
      value: snapshot.connected ? "Conectado" : "Desconectado",
    });
  }
  snapshotEntries.push(...flattenDetails(snapshot.status || {}, "Snapshot"));
  return [...details, ...snapshotEntries];
}

export function historyDetailsLabel(row) {
  const entries = historyDetailEntries(row);
  if (entries.length === 0) return "—";
  return entries.map((entry) => `${entry.label}: ${entry.value}`).join(" | ");
}

export function historyReceivedDate(row) {
  const date = new Date(row?.created_at);
  return Number.isNaN(date.getTime()) ? null : date;
}
